import { getLocalTimeZone } from "@internationalized/date"
import { createFileRoute, Link } from "@tanstack/react-router"
import { PaymentFlow, ReservationStatus } from "ipctg-inflow-types/InFlowEnums"
import type { ReservationMini } from "ipctg-inflow-types/InFlowReserveAPI"
import { useTranslation } from "react-i18next"

import { LinkButton } from "@/globalComponents/atoms/Button"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import { ReservationConfirmationCard } from "@/globalComponents/molecules/productCards/ReservationCard"
import {
  confirmReservePaymentQueryOptions,
  confirmReserveSetupIntentQueryOptions,
} from "@/utilities/hooks/queries/reserve"
import { receiptQueryOptions } from "@/utilities/hooks/queries/shared"
import { useTheme } from "@/utilities/hooks/useTheme"

export const Route = createFileRoute(
  "/qr/$qrLookupID/reserve/$epoch/calendars/$calendarID/dates/$year/$month/$day/confirmation"
)({
  staticData: {
    pageName: "Confirmation",
  },
  beforeLoad: async ({ context }) => {
    const payRsp = await context.queryClient.fetchQuery(
      context.sessionState.transactionDetails?.paymentFlow ===
        PaymentFlow.SetupIntent
        ? confirmReserveSetupIntentQueryOptions()
        : confirmReservePaymentQueryOptions()
    )
    const receiptRsp = await context.queryClient.fetchQuery(
      receiptQueryOptions({ receiptID: payRsp.receiptID })
    )
    return { receipt: receiptRsp.receipt }
  },
  component: RouteComponent,
})

function RouteComponent() {
  const { receipt } = Route.useRouteContext()
  const { qrLookupID } = Route.useParams()
  const { t } = useTranslation("reserve")
  const { locationInformation } = useTheme()
  const locationAddress = locationInformation?.address || {
    address: "",
    placeID: "",
    coordinates: null,
  }
  const timezoneLocation = locationInformation?.timeZone || getLocalTimeZone()

  return (
    <>
      <Heading level={1} className="center">
        {t("confirmation.header")}
      </Heading>
      <section className="module">
        <ReservationConfirmationCard
          reservation={
            {
              reservationID: receipt.receiptID,
              lotName: receipt.lotName,
              bookingCalendarName: receipt.bookingCalendarName!,
              inventoryTreeRateName: receipt.rateName,
              startDateTime: receipt.startTime,
              endDateTime: receipt.endTime,
              plate: receipt.plate,
              locationName: receipt.locationName,
              locationID: "",
              marketID: "",
              enterpriseID: "",
              amountPaid: receipt.amountPaid,
              receiptIDs: [receipt.receiptID],
              status: ReservationStatus.Active,
              allowPlateChangeUntil: null,
              cancellationDeadline: null,
              locationTimezone: timezoneLocation,
              locationAddress: locationAddress,
              confirmationNumber: "",
              usageStatus: null,
              blockCancellation: false,
            } satisfies ReservationMini
          }
          variant="confirmation"
        />
        <LinkButton
          buttonType="primary"
          presentation="filled"
          to="/qr/$qrLookupID/receipt/$receiptID"
          params={{ qrLookupID, receiptID: receipt.receiptID }}
        >
          <Icon name="receipt" slot="left" />
          {t("viewReceipt", { ns: "receipt" })}
        </LinkButton>
      </section>
      <Link
        to="/qr/$qrLookupID/welcome"
        params={{ qrLookupID }}
        className="center semi-bold body--2"
      >
        {t("common.returnToPurchaseMenu", { ns: "common" })}
      </Link>
    </>
  )
}
